import { type AccessTokenType, FeatureFlags, User } from "@finway-group/shared/lib/models"
import jwt from "jsonwebtoken"
import { useEffect, useMemo, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory, useLocation } from "react-router-dom"
import { PROCESS_ENV } from "~Shared/config/env.utils"
import { useLocalStorage } from "~Shared/hooks/useLocalStorage"
import { RoutePathEnum } from "~Shared/router/RoutePath.enum"
import { AuthnService } from "~Shared/services/authn.service"
import { RESET_REDUX_STORE_EVENT } from "~Shared/services/localstorage.consts"
import { ACCESS_TOKEN, LocalStorageService } from "~Shared/services/localstorage.service"
import { NotificationService } from "~Shared/services/notification.service"
import { SessionStorageService } from "~Shared/services/sessionstorage.service"
import { createLoginAction, resetReduxStore } from "~Shared/store/actions/auth/authActions"
import { fetchAllCompanies } from "~Shared/store/actions/company/companyActions"
import { setFeatureFlags } from "~Shared/store/actions/featureFlags/featureFlagsActions"
import { fetchApiVersion } from "~Shared/store/actions/fetcher/fetcherActions"
import { type RootState } from "~Shared/store/rootState.interface"
import { generatePath } from "~Shared/utils/routing.utils"

export const useAuthWithNavigation = () => {
    const { isAuthenticated, userId, userClass, isSsoLogin, rights, isFinwayAdmin, featureFlags, companyId, logout } = useAuth()
    const dispatch = useDispatch()

    const previousAuthenticatedState = useRef(!!LocalStorageService.getAccessToken())
    const history = useHistory()
    const location = useLocation()
    const isAccessTokenInRedux = !!useSelector(({ auth: { accessToken } }: RootState) => accessToken)
    const authFlowPaths = [generatePath(RoutePathEnum.LOGIN), generatePath(RoutePathEnum.FORGOT_PASSWORD), generatePath(RoutePathEnum.NEW_PASSWORD)]
    const isRedirectingFromAuthFlow = authFlowPaths.includes(location.pathname) || location.pathname.startsWith("/reset/")

    const redirect = () => {
        NotificationService.clear()

        /**
         * sso logins
         */
        if (!isSsoLogin && userClass?.passwordExpired) {
            history.push(generatePath(RoutePathEnum.NEW_PASSWORD))
            return
        }

        /**
         * in-app re-auth redirects
         */
        if (window.history.state && window.history.state.idx > 0) {
            history.goBack()
            return
        }
        // redirect from login page
        const redirectUrl = SessionStorageService.getRedirectUrl()
        if (redirectUrl) {
            // Avoid leaving /login in browser history.
            history.replace({ pathname: redirectUrl, state: { fromAuthRedirect: isRedirectingFromAuthFlow } })
            return
        }

        history.replace({ pathname: generatePath(RoutePathEnum.ROOT), state: { fromAuthRedirect: isRedirectingFromAuthFlow } })
    }

    /**
     * automatically re-direct from /login if logged-in
     */
    useEffect(() => {
        if (location.pathname === "/login" && isAuthenticated) {
            redirect()
        }
    }, [location, isAuthenticated])

    useEffect(() => {
        /**
         * trigger login flow once accessToken has been found
         */
        if (isAuthenticated === true && previousAuthenticatedState.current === false && featureFlags) {
            const token = LocalStorageService.getAccessToken()
            /* In case the user has two tabs open and they login on the first one, we should manually create a login action on the other */
            if (token && !isAccessTokenInRedux) {
                const loginAction = createLoginAction(token)
                dispatch(loginAction)
            }
            dispatch(fetchApiVersion())
            dispatch(setFeatureFlags(featureFlags.getFlagsObject()))
            dispatch(fetchAllCompanies())
            redirect()
        }

        /**
         * perform proper logout
         */
        if (isAuthenticated === false && previousAuthenticatedState.current === true) {
            LocalStorageService.unsetAccessToken()
        }

        // update ref
        previousAuthenticatedState.current = isAuthenticated
    }, [isAuthenticated])

    useEffect(() => {
        const handleResetReduxStore = () => {
            dispatch(resetReduxStore())
        }
        window.addEventListener(RESET_REDUX_STORE_EVENT, handleResetReduxStore)
        return () => {
            window.removeEventListener(RESET_REDUX_STORE_EVENT, handleResetReduxStore)
        }
    }, [])

    return {
        logout,
        isAuthenticated,
        isFinwayAdmin,
        rights,
        userId,
        userClass,
        isSsoLogin,
        featureFlags,
        companyId,
    }
}

export const useAuth = () => {
    const { userClass, isSsoLogin, activeRights, featureFlags, companyId } = useAccessTokenData()
    const isAuthenticated = useMemo(() => !!userClass?.email, [userClass?.email])

    const logout = async () => {
        await AuthnService.logout()
        LocalStorageService.unsetAccessToken()
    }

    return {
        isAuthenticated,
        userId: userClass?.id,
        userClass,
        isSsoLogin,
        rights: activeRights,
        featureFlags,
        companyId,
        isFinwayAdmin: userClass?.email === PROCESS_ENV.FINWAY_ADMIN_EMAIL,
        logout,
    }
}

const useAccessToken = () => {
    const [accessToken, setEncodedAccessToken] = useLocalStorage(ACCESS_TOKEN, "")

    return {
        encodedAccessToken: accessToken,
        decodedAccessToken: accessToken ? (jwt.decode(accessToken) as AccessTokenType) : undefined,
        setEncodedAccessToken,
    }
}

const useAccessTokenData = () => {
    const { decodedAccessToken } = useAccessToken()

    const userClass = decodedAccessToken ? User.newFromAccessToken(decodedAccessToken) : undefined
    const featureFlags = decodedAccessToken?.featureFlags ? new FeatureFlags(decodedAccessToken.featureFlags) : undefined
    const flagsTmp = decodedAccessToken?.flagsTmp ?? {}

    return {
        userClass,
        companyId: decodedAccessToken?.currentCompanyId,
        featureFlags,
        flagsTmp,
        isSsoLogin: Boolean(decodedAccessToken?.isSsoLogin) === true,
        activeRights: decodedAccessToken?.activeRights,
    }
}

export const useFlagsTmp = () => useAccessTokenData().flagsTmp

export const useIsAiReportingEnabled = () => useFlagsTmp().isAiReportingEnabled === true
export const useIsApiKeyManagementEnabled = () => useFlagsTmp().isApiKeyManagementEnabled === true

export const useWeavrAuth = () => {
    const { isWeavrAuthenticated, shouldShowWeavrAuthModal, isWeavrUserOnBoard } = useSelector(({ auth }: RootState) => auth)

    return {
        isWeavrAuthenticated,
        shouldShowWeavrAuthModal,
        isWeavrUserOnBoard,
    }
}
